import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { MemberMetric } from "types/BusinessDashboard";
import type { MemberMonthlyParams } from "api/businessDashboard";
import { MEMBERS_PASS_TYPE_MAP } from "constants/businessDashboard";

import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import { useMemberDetailQuery } from "queries/Business/Dashboard/useDashboardQueries";

import { MembersStatCard } from "./members-stat-card";
import { MembersDashboardSummaryError } from "./members-dashboard-summary.error";
import { MembersDashboardSummarySkeleton } from "./members-dashboard-summary.skeleton";

dayjs.locale("ko");

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 16px;
  padding-top: 20px;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 12px;
  background: #fff;
  position: relative;
`;

export const MembersDashboardSummary = () => {
  const { searchParams } = useUpdateUrlParams();

  const endMonthParam =
    searchParams.get("endMonth") || dayjs().format("YYYY-MM");
  const endDate = dayjs(endMonthParam);

  const referenceEndMonth = useMemo(() => endDate.format("YYYY-MM"), [endDate]);
  const referenceStartMonth = useMemo(
    () => endDate.subtract(5, "month").format("YYYY-MM"),
    [endDate]
  );

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: MemberMonthlyParams = {
      referenceStartMonth,
      referenceEndMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [partnerInfo, referenceEndMonth, referenceStartMonth]);

  const {
    data: detailData,
    isLoading,
    isError,
    refetch,
  } = useMemberDetailQuery(apiParams);

  const handleRetry = () => {
    refetch();
  };

  const { stats } = (() => {
    if (!detailData) return { stats: {} };

    const statsResult = {
      total: detailData.summary.total,
      pt: detailData.summary.personalLesson,
      groupClass: detailData.summary.groupClass,
      membership: detailData.summary.membership,
      option: detailData.summary.option,
    };

    return { stats: statsResult };
  })();

  if (isLoading) return <MembersDashboardSummarySkeleton />;
  if (isError || !detailData)
    return <MembersDashboardSummaryError onRetry={handleRetry} />;

  return (
    <Wrapper>
      {Object.entries(stats)
        .slice(0, 1)
        .map(([key, metric]) => {
          const uiInfo = MEMBERS_PASS_TYPE_MAP[key];
          if (!uiInfo) return null;
          return (
            <MembersStatCard
              big
              key={key}
              title="전체 유효 회원수"
              metric={metric as MemberMetric}
              color={uiInfo.color}
              bgColor={uiInfo.bgColor}
              className="h-[140px]"
            />
          );
        })}
      <GridWrapper>
        {Object.entries(stats)
          .slice(1, 5)
          .map(([key, metric]) => {
            const uiInfo = MEMBERS_PASS_TYPE_MAP[key];
            if (!uiInfo) return null;
            return (
              <MembersStatCard
                key={key}
                big={key === "total"}
                title={
                  key === "membership"
                    ? "회원권 (시설이용)"
                    : key === "groupClass"
                      ? "그룹수업"
                      : key === "pt"
                        ? "개인레슨"
                        : uiInfo.title
                }
                metric={metric as MemberMetric}
                color={uiInfo.color}
                bgColor={uiInfo.bgColor}
                className="h-[140px]"
              />
            );
          })}
      </GridWrapper>
    </Wrapper>
  );
};
